import { defineStore } from "pinia";
import { LocalStorageFilterOptions } from "../types/models";
import { useLoadingStore } from "./loading";
import MessageBox from "../services/MessageBox";

export const listManager = defineStore("selectLists", () => {
  const dbName = "HealthPackDBs";
  const loading = useLoadingStore();

  function openDB(store: string, keypath: string = "id", indexes: string[] = []): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(dbName);

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        if (!db.objectStoreNames.contains(store)) {
          const objectStore = db.createObjectStore(store, { keyPath: keypath });
          indexes.forEach(index => objectStore.createIndex(index, index, { unique: false }));
        }
      };

      request.onsuccess = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        if (!db.objectStoreNames.contains(store)) {
          db.close();
          const versionRequest = indexedDB.open(dbName, db.version + 1);
          versionRequest.onupgradeneeded = (event) => {
            const upgradeDb = (event.target as IDBOpenDBRequest).result;
            const objectStore = upgradeDb.createObjectStore(store, { keyPath: keypath });
            indexes.forEach(index => objectStore.createIndex(index, index, { unique: false }));
          };
          versionRequest.onsuccess = () => resolve(versionRequest.result);
          versionRequest.onerror = () => reject(versionRequest.error);
        } else {
          resolve(db);
        }
      };

      request.onerror = () => reject(request.error);
    });
  }

  function delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  /*async function storeListData(listName: string, data: any[]): Promise<void> {
    const db = await openDB(listName);
    const batchSize = 50;

    for (let i = 0; i < data.length; i += batchSize) {
      const transaction = db.transaction(listName, "readwrite");
      const store = transaction.objectStore(listName);
      const batch = data.slice(i, i + batchSize);

      for (const item of batch) {
        await new Promise<void>((resolve, reject) => {
          const request = store.put(item);
          request.onsuccess = () => {
            console.log('saved', listName, item);
            //loading.updateProgress();
            resolve();
          };
          request.onerror = () => reject(request.error);
        });

        await delay(0); // Allow the event loop to process
      }

      await new Promise((resolve, reject) => {
        transaction.oncomplete = () => {
          resolve(null);
        };
        transaction.onerror = () => reject(transaction.error);
      });
    }

    db.close();
    loading.stopLoading();
  }*/

  async function storeListDataItem(listName: string, data: any): Promise<void> {
    const db = await openDB(listName);

    const transaction = db.transaction(listName, "readwrite");
      const store = transaction.objectStore(listName);
      
      await new Promise<void>((resolve, reject) => {
        const request = store.put(data);
        request.onsuccess = () => {
          console.log('saved', listName, data);
          //loading.updateProgress();
          resolve();
        };
        request.onerror = () => reject(request.error);
      });

      await new Promise((resolve, reject) => {
        transaction.oncomplete = () => {
          resolve(null);
        };
        transaction.onerror = () => reject(transaction.error);
      });

    db.close();
  }

  async function saveList(listName: string, data: any[]): Promise<void> {
    //loading.startLoading(`Configuring ${moduleToTitle(listName)}...`, data.length);
    loading.startLoading();
    try {
      await openDB(listName);
      console.log('db opened on line 87')
      for (const el of data) {
        await storeListDataItem(listName, el);
        //await storeListData(listName, data);
        //loading.updateProgress(); TODO: check this later
        //await delay(10); 
        //await delay(1000);
      }
      //await storeListData(listName, data);
    } catch (error) {
      console.error("Error Saving to Offline Storage", error);
      throw new Error(`Error Saving ${listName} to Offline Storage`);
    } finally {
      loading.stopLoading();
    }
  }

  const getList = async (
    listName: string,
    filters?: LocalStorageFilterOptions[],
    searchQuery?: string
  ): Promise<any[]> => {
    const db = await openDB(listName);
    const transaction = db.transaction(listName, "readonly");
    const store = transaction.objectStore(listName);
    const searchResults: any[] = [];

    const request = store.openCursor();

    return new Promise((resolve, reject) => {
      request.onsuccess = (event) => {
        const cursor = (event.target as IDBRequest).result;
        if (cursor) {
          const item = cursor.value;

          if (filters && filters.length > 0) {
            let matches;
            if (searchQuery) {
              matches = filters.reduce((acc, query, index) => {
                const { field_name, match_type } = query;
                if (item[field_name] === undefined) return acc;

                let match = false;
                switch (match_type) {
                  case "=":
                    match = String(item[field_name]).toLowerCase() === String(searchQuery).toLowerCase();
                    break;
                  case "like":
                    match = item[field_name].toLowerCase().includes(String(searchQuery).toLowerCase());
                    break;
                  case ">":
                    match = searchQuery !== undefined && item[field_name] > searchQuery;
                    break;
                  case "<":
                    match = searchQuery !== undefined && item[field_name] < searchQuery;
                    break;
                  default:
                    match = false;
                }

                return index === 0 ? match : acc || match;
              }, false) ?? true;
            } else {
              matches = filters.every((query) => {
                const { field_name, match_type, value } = query;
                if (item[field_name] === undefined) return false;

                let match = false;
                switch (match_type) {
                  case "=":
                    match = String(item[field_name]).toLowerCase() === String(value).toLowerCase();
                    break;
                  case "like":
                    match = item[field_name].toLowerCase().includes(String(value).toLowerCase());
                    break;
                  case ">":
                    match = value !== undefined && value !== null && item[field_name] > value;
                    break;
                  case "<":
                    match = value !== undefined && value !== null && item[field_name] < value;
                    break;
                  default:
                    match = false;
                }

                return match;
              });
            }

            if (matches || searchQuery?.length === 0) {
              searchResults.push(item);
            }
          } else {
            searchResults.push(item);
          }

          cursor.continue();
        } else {
          db.close();
          resolve(searchResults);
        }
      };

      request.onerror = () => {
        db.close();
        reject(request.error);
      };
    });
  };

  const clearDB = (): Promise<string> => {
    return new Promise((resolve, reject) => {
      const request = indexedDB.deleteDatabase(dbName);

      request.onsuccess = function () {
        loading.stopLoading();
        MessageBox.show("Offline Data Cleared successfully", 'info');
        resolve(`Offline Data Cleared successfully`);
      };

      request.onerror = function () {
        loading.stopLoading();
        MessageBox.show(`Error Clearing Offline Data`, 'error');
        reject(`Error Clearing Offline Data`);
      };

      request.onblocked = async function () {
        await delay(5000);
        clearDB();
      };
    });
  };

  const closeAllConnections = () => {
    return new Promise((resolve, reject) => {
      loading.startLoading();
      const request = indexedDB.open(dbName);

      request.onsuccess = function (event) {
        const db = (event.target as IDBOpenDBRequest).result;

        // Close the connection
        db.close();

        // Retry deleting the database
        clearDB()
          .then((data) => {
            loading.stopLoading();
            resolve(data);
          })
          .catch((error) => {
            loading.stopLoading();
            reject(error);
          });
      };

      request.onerror = function () {
        loading.stopLoading();
        MessageBox.show(`Error Clearing Offline Data Connections`, 'error');
        reject(`Error Clearing Offline Data Connections`);
      };
    });
  };

  return {
    saveList,
    getList,
    clearDB,
    closeAllConnections,
  };
});